import React, { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Dropdown from "../components/Dropdown";
import { MobileResponsive } from "./MobileResponsive";
import DynamicMetaTags from "../components/DynamicMetaTags";
import useDataFetch from "../hooks/useDataFetch";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Europe/London",
  });
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      onClick={handleCopy}
      title="Copy Stripe ID"
      className={`ml-2 px-2 py-0.5 rounded text-xs font-semibold border transition-colors ${
        copied
          ? "border-green-400 text-green-600 bg-green-50"
          : "border-gray-300 text-gray-500 hover:border-quaternary hover:text-quaternary"
      }`}
    >
      {copied ? "Copied!" : "Copy"}
    </button>
  );
};

const OutletWeeklyEarnings = () => {
  const { id } = useParams();
  const monthContainerRef = useRef(null);

  const currentYear = new Date().getFullYear();
  const baseYearOptions = ["2024", "2023"];
  const yearOptions = [...new Set([...baseYearOptions, currentYear.toString()])].sort((a, b) => b - a);

  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [month, setMonth] = useState(MONTHS[new Date().getMonth()]);
  const [showArrows, setShowArrows] = useState(false);

  const monthNumber = MONTHS.indexOf(month) + 1;

  const { data, isLoading } = useDataFetch(
    `/admin/outlet-payout-records?month=${monthNumber}&year=${selectedYear}&outletId=${id}`
  );

  const records = data?.records || [];
  const total = records.reduce((sum, r) => sum + (r.paymentAmount || 0), 0);

  const checkArrows = () => {
    if (monthContainerRef.current) {
      setShowArrows(
        monthContainerRef.current.scrollWidth > monthContainerRef.current.clientWidth
      );
    }
  };

  React.useEffect(() => {
    checkArrows();
    window.addEventListener("resize", checkArrows);
    return () => window.removeEventListener("resize", checkArrows);
  }, []);

  const scrollLeft = () => monthContainerRef.current?.scrollBy({ left: -200, behavior: "smooth" });
  const scrollRight = () => monthContainerRef.current?.scrollBy({ left: 200, behavior: "smooth" });

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Outlet Payout Records"
        pageDescription="View outlet payout records and earnings history."
        pageKeywords="Beep, outlet payouts, earnings, payout records"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary">
          {/* Header */}
          <div className="flex justify-between w-full mb-8">
            <Link to={`/outlets/${id}`} className="flex items-center gap-3">
              <div className="bg-[#F75E00] w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px]">
                <img src="/assets/left-white-arrow.svg" alt="" className="w-[6px]" />
              </div>
              <span className="font-semibold text-base">Payout Records</span>
            </Link>

            <Dropdown
              options={yearOptions}
              selected={selectedYear}
              onSelect={setSelectedYear}
              label="Select Year"
            />
          </div>

          {/* Month picker */}
          <div className="flex items-center gap-2 mb-6">
            {showArrows && (
              <button
                className="bg-[#F75E00] w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px] flex-shrink-0"
                onClick={scrollLeft}
              >
                <img src="/assets/left-white-arrow.svg" alt="" className="w-[6px]" />
              </button>
            )}
            <div
              ref={monthContainerRef}
              className="flex items-center w-full overflow-x-auto space-x-1 hide-scroll-bar"
              style={{ scrollBehavior: "smooth" }}
            >
              {MONTHS.map((m, index) => (
                <React.Fragment key={m}>
                  <button
                    onClick={() => setMonth(m)}
                    className={`px-4 py-1 rounded whitespace-nowrap ${
                      month === m ? "text-primary font-bold" : "font-medium text-[#8D8D8D]"
                    }`}
                  >
                    {m}
                  </button>
                  {index < MONTHS.length - 1 && <span className="text-gray-300">|</span>}
                </React.Fragment>
              ))}
            </div>
            {showArrows && (
              <button
                className="bg-[#F75E00] rotate-180 w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px] flex-shrink-0"
                onClick={scrollRight}
              >
                <img src="/assets/left-white-arrow.svg" alt="" className="w-[6px]" />
              </button>
            )}
          </div>

          {/* Records table */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-10 w-10 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin" />
            </div>
          ) : (
            <div className="border-2 border-t-4 border-quaternary rounded-xl bg-[#F7F9F8] overflow-x-auto">
              <table className="w-full table-auto divide-y divide-gray-200">
                <thead>
                  <tr>
                    {["Date", "Method", "Card", "Amount", "Stripe ID"].map((h) => (
                      <th key={h} className="text-start text-sm font-bold px-5 py-3 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#8D8D8D]">
                  {records.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-10 text-center text-sm text-gray-500 italic">
                        No payout records for {month} {selectedYear}.
                      </td>
                    </tr>
                  ) : (
                    records.map((record, index) => (
                      <tr key={record._id || index} className={index % 2 === 0 ? "bg-gray-100" : ""}>
                        <td className="px-5 py-3 text-sm font-medium whitespace-nowrap">
                          {formatDate(record.createdAt)}
                        </td>
                        <td className="px-5 py-3 text-sm font-medium">
                          <span className="px-2 py-0.5 rounded-full bg-orange-100 text-quaternary text-xs font-semibold capitalize">
                            {record.paymentMethod || "—"}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-sm font-medium whitespace-nowrap">
                          {record.cardLast4 ? (
                            <span className="flex items-center gap-1">
                              <span className="capitalize text-gray-500 text-xs">
                                {record.paymentMethodBrand || ""}
                              </span>
                              <span className="font-mono">••••&nbsp;{record.cardLast4}</span>
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-sm font-bold text-gray-800 whitespace-nowrap">
                          £{Number(record.paymentAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-5 py-3 text-sm font-medium">
                          <div className="flex items-center">
                            <span className="font-mono text-xs text-gray-600 truncate max-w-[200px]">
                              {record.stripePaymentID || "—"}
                            </span>
                            {record.stripePaymentID && (
                              <CopyButton text={record.stripePaymentID} />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {records.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-quaternary">
                      <td colSpan={3} className="px-5 py-3 text-sm font-bold text-gray-700">
                        Total — {records.length} payout{records.length !== 1 ? "s" : ""}
                      </td>
                      <td className="px-5 py-3 text-sm font-bold text-gray-900">
                        £{total.toFixed(2)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OutletWeeklyEarnings;
